import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  EstimateTransactionFeeDTO,
  EstimateTransactionFeeResult,
  useGetTransitAddressesQuery,
  useLazyEstimateTransactionFeeQuery,
} from '../../api';
import { InfoField } from '../../components';
import { UTCToDateTimeWithSecond } from '../../utils/dates';
import { blockchainAddressMap } from './constants';
import type { TODO_ANY } from '../../types';
import { formatAmount, formatBlockchainAmountWithCurrency } from '../../utils';

export const BlockchainFee: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // api
  const { data: transitAddresses, isLoading: isTransitAddressesLoading } =
    useGetTransitAddressesQuery();
  const [estimateTransactionFee, { isLoading: isEstimateLoading, isFetching }] =
    useLazyEstimateTransactionFeeQuery();

  // variables
  const isLoading = isTransitAddressesLoading || isEstimateLoading;
  const [estimateData, setEstimateData] = useState<EstimateTransactionFeeResult | null>(null);

  // fields logic
  const [fields, setFields] = useState<TODO_ANY | null>(null);
  const handleFields = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = e.target.name as keyof EstimateTransactionFeeDTO['body'];
    const value = e.target.value;

    // @ts-ignore
    setFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };
  const resetFields = () => {
    setFields(null);
    setEstimateData(null);
  };

  // validation for required fields
  const requiredFields = ['addressId', 'target_address', 'amount'] as (
    | keyof EstimateTransactionFeeDTO['body']
    | 'addressId'
  )[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '') {
        return true;
      }
    }
    if (fields.amount < 0) return true;

    return false;
  };

  // autocomplete for target address
  useEffect(() => {
    const currentTransitAddress = transitAddresses?.addresses.find(
      (address) => address.id === fields?.addressId
    );
    const prefilledBlockchains = Object.keys(blockchainAddressMap);

    if (
      currentTransitAddress &&
      prefilledBlockchains.includes(currentTransitAddress.coin.blockchain)
    ) {
      setFields({
        ...fields,
        target_address: blockchainAddressMap[currentTransitAddress.coin.blockchain],
      });
    }
  }, [fields?.addressId]);

  // autocomplete for amount
  useEffect(() => {
    setFields({ ...fields, amount: 1 });
  }, []);

  // estimate fee handler
  const estimateFee = async () => {
    if (!fields) return;

    const args = {
      addressId: fields.addressId,
      body: {
        target_address: fields.target_address,
        amount: String(fields.amount),
      },
    };

    try {
      const res = await estimateTransactionFee(args).unwrap();
      setEstimateData(res);
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // elements
  const addressesOptions = transitAddresses?.addresses.map((address) => {
    return (
      <MenuItem key={address.id} value={address.id}>
        {address?.coin?.name}
      </MenuItem>
    );
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Stack direction="column" spacing={2}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Stack direction="column" spacing={2} maxWidth={360}>
              <TextField
                select
                name="addressId"
                value={fields?.addressId ?? ''}
                onChange={(e) => handleFields(e)}
                label={t('blockchain-fee.fields.coin')}
                fullWidth
              >
                {addressesOptions ?? []}
              </TextField>

              <TextField
                name="target_address"
                value={fields?.target_address ?? ''}
                onChange={(e) => handleFields(e)}
                label={t('blockchain-fee.fields.target-address')}
                fullWidth
              />

              <TextField
                name="amount"
                type="number"
                value={fields?.amount ?? ''}
                onChange={(e) => handleFields(e)}
                inputProps={{ min: 0 }}
                label={t('blockchain-fee.fields.amount')}
                fullWidth
              />

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={() => estimateFee()}
                  disabled={isLoading || checkSubmitDisabling()}
                  fullWidth
                >
                  {t('blockchain-fee.estimate-btn')}
                </Button>

                <Button variant="outlined" onClick={() => resetFields()} fullWidth>
                  {t('blockchain-fee.reset-btn')}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {estimateData && (
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="column" spacing={2}>
                <Typography variant="h6"> {t('blockchain-fee.calc.title')}:</Typography>
                {isFetching ? (
                  <Box style={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <InfoField
                      name={t('blockchain-fee.calc.requested-at')}
                      value={estimateData?.requested_at}
                      formattedValue={
                        estimateData?.requested_at
                          ? UTCToDateTimeWithSecond(estimateData.requested_at as string)
                          : undefined
                      }
                    />
                    <InfoField
                      name={t('blockchain-fee.calc.blockchain-fee')}
                      value={estimateData?.fee}
                      formattedValue={
                        estimateData?.fee
                          ? formatBlockchainAmountWithCurrency(
                              estimateData.fee,
                              estimateData.coin.blockchain
                            )
                          : undefined
                      }
                    />
                    <InfoField
                      name={t('blockchain-fee.calc.exchange-rate')}
                      value={undefined}
                      withEmptyValue
                      formattedValue={formatAmount(estimateData?.rate)}
                    />
                    <InfoField
                      name={t('blockchain-fee.calc.converted-fee')}
                      value={estimateData?.convertedAmount}
                      formattedValue={
                        estimateData?.convertedAmount
                          ? formatBlockchainAmountWithCurrency(
                              estimateData.convertedAmount,
                              estimateData.coin.code
                            )
                          : undefined
                      }
                      withoutDivider
                    />
                  </>
                )}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Grid>
    </>
  );
};
